import { array } from 'fp-ts';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { GroupOption, Option } from '@mews-commander/types';
import { UnicodeEntity } from '@mews/enums';
import { FormSelectField, FormSelectFieldProps } from '@mews/framework';
import { DropdownItem, OptionGroup, Selected } from '@optimus-web/core';

export const SELECT_EMPTY_VALUE = '';

export type SelectFieldProps = Omit<FormSelectFieldProps, 'children'> & {
    autoSelectOnlyOption?: boolean;
    showEmptyValue?: boolean;
    emptyValueLabel?: React.ReactNode;
    emptyValueAsNull?: boolean;
    showSelectAll?: boolean;
    options: Option[] | GroupOption[];
};

const isGroupOptions = (options: GroupOption[] | Option[]): options is Array<GroupOption> =>
    (options[0] as GroupOption)?.groupOptions !== undefined;

const useSelectIfOneOption = ({
    autoSelectOnlyOption,
    options,
    name,
}: Pick<SelectFieldProps, 'autoSelectOnlyOption' | 'options' | 'name'>) => {
    const { setValue } = useFormContext<{ [name: string]: string }>();

    useEffect(() => {
        if (autoSelectOnlyOption && options.length === 1) {
            if (isGroupOptions(options)) {
                if (options[0].groupOptions.length === 1) {
                    setValue(name, options[0].groupOptions[0].value ?? '');
                }
            } else {
                setValue(name, options[0].value ?? '');
            }
        }
    }, [autoSelectOnlyOption, options, name, setValue]);
};

export const SelectField = ({
    autoSelectOnlyOption,
    multiselect,
    name,
    options,
    required,
    showEmptyValue: showEmptyValueParam,
    emptyValueLabel,
    emptyValueAsNull,
    showSelectAll,
    transform,
    ...rest
}: SelectFieldProps) => {
    const showEmptyValue = showEmptyValueParam ?? (!multiselect && !required);

    useSelectIfOneOption({ autoSelectOnlyOption, options, name });

    return (
        <FormSelectField
            {...rest}
            data-test-select-name={name}
            name={name}
            multiselect={multiselect}
            required={required}
            selectAllEnabled={showSelectAll}
            transform={
                emptyValueAsNull
                    ? {
                          input: (value) =>
                              value === SELECT_EMPTY_VALUE || (Array.isArray(value) && array.isEmpty(value))
                                  ? null
                                  : transform?.input?.(value) ?? value,
                          output: (value) => {
                              if (value === null) {
                                  return multiselect ? [] : SELECT_EMPTY_VALUE;
                              }
                              return transform?.output?.(value) ?? (value as Selected);
                          },
                      }
                    : transform
            }
        >
            {showEmptyValue && (
                <DropdownItem
                    data-test-select-item-value={SELECT_EMPTY_VALUE}
                    value={SELECT_EMPTY_VALUE}
                    label={emptyValueLabel || UnicodeEntity.Dash}
                />
            )}

            {isGroupOptions(options) ? options.map(getGroupElements) : getOptionElements(options)}
        </FormSelectField>
    );
};

const getGroupElements = (group: GroupOption, index: number) => [
    <OptionGroup label={group.groupLabel} groupId={`${index}`}>
        {group.groupOptions && getOptionElements(group.groupOptions)}
    </OptionGroup>,
];

const getOptionElements = (options: Option[]) =>
    options.map(({ value, label, secondaryText, disabled, ...option }) => (
        <DropdownItem
            key={value}
            data-test-select-item-value={value}
            value={value ?? ''}
            label={label}
            description={secondaryText}
            disabled={disabled}
            {...option}
        />
    ));
